import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Loader2, RefreshCcw } from "lucide-react";

import { apiFetch } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { SlotConfigurator } from "@/pages/kiosk/components/SlotConfigurator";
import { CameraCapture } from "@/pages/kiosk/components/CameraCapture";
import { QrCode } from "@/pages/kiosk/components/QrCode";

type ScenePack = {
  id: string;
  title: string;
  description?: string;
  tags_json?: string;
  allowed_styles_json?: string;
  prompt_template?: string;
  imageUrl: string;
};

type Cameo = {
  id: string;
  name: string;
  description?: string;
  cameo_prompt_snippet?: string;
  imageUrl: string;
};

type CatalogResponse = {
  tenant: {
    id: string;
    name: string;
    currency: string;
    price_per_image_paise: number;
    price_per_video_paise: number | null;
  };
  scenePacks: ScenePack[];
  cameos: Cameo[];
};

type SessionStatusResponse = {
  session: {
    id: string;
    status: string;
    purchased_count: number | null;
    image_credits_remaining: number;
    regen_tokens_remaining: number;
    created_at: string;
    updated_at: string;
    hasSelfie: boolean;
    configuredSlots: number;
    totalSlots: number;
    allSlotsConfigured: boolean;
    videoEnabled: boolean;
  };
  slots: Array<{
    id: string;
    slot_index: number;
    status: string;
    recipe: any;
    imageUrl: string | null;
    last_job_id: string | null;
    isVideo: boolean;
    // Per-slot config
    scenePackId: string | null;
    cameoIds: string[];
    style: string | null;
    goofyPrompt: string | null;
  }>;
};

type Step = "pack" | "selfie" | "slots" | "pay" | "preview" | "qr";

function nextStep(step: Step): Step {
  const order: Step[] = ["pack", "selfie", "slots", "pay", "preview", "qr"];
  const idx = order.indexOf(step);
  return order[Math.min(order.length - 1, idx + 1)];
}

export function KioskPage() {
  const [step, setStep] = useState<Step>("pack");
  const [catalog, setCatalog] = useState<CatalogResponse | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);

  const [packCount, setPackCount] = useState(10);
  const [videoEnabled, setVideoEnabled] = useState(false);

  const imagesEnabled = packCount > 0;

  // Slot configuration state
  const [currentSlotIndex, setCurrentSlotIndex] = useState(0);
  const [slotConfigs, setSlotConfigs] = useState<
    Record<
      number,
      {
        scenePackId: string | null;
        cameoIds: string[];
        style: string | null;
        goofyPrompt: string | null;
      }
    >
  >({});

  const [sessionStatus, setSessionStatus] =
    useState<SessionStatusResponse | null>(null);
  const [downloadUrl, setDownloadUrl] = useState<string>("");
  const [busy, setBusy] = useState(false);
  // Separate busy state for video purchase to avoid blocking other interactions if needed,
  // but 'busy' is generally used for global blocking. We'll use 'busy' for simplicity.

  async function createFreshSession() {
    setBusy(true);
    try {
      const res = await apiFetch<{ sessionId: string; videoEnabled: boolean }>(
        "/api/kiosk/sessions",
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ desiredPackCount: packCount, videoEnabled }),
        },
      );
      localStorage.setItem("memorix:kioskSessionId", res.sessionId);
      setSessionId(res.sessionId);
      setVideoEnabled(res.videoEnabled);
      setSessionStatus(null);
      setDownloadUrl("");
      setSlotConfigs({});
      setCurrentSlotIndex(0);
      setStep("pack");
    } finally {
      setBusy(false);
    }
  }

  useEffect(() => {
    (async () => {
      try {
        const c = await apiFetch<CatalogResponse>("/api/kiosk/catalog");
        setCatalog(c);
      } catch (err: any) {
        toast.error("Failed to load catalog", {
          description: err?.body?.error || err?.message,
        });
      }
    })();
  }, []);

  useEffect(() => {
    const existing = localStorage.getItem("memorix:kioskSessionId");
    if (existing) {
      // Verify session still exists before using it
      apiFetch<SessionStatusResponse>(`/api/kiosk/sessions/${existing}`)
        .then((s) => {
          setSessionId(existing);
          setSessionStatus(s);
        })
        .catch(() => {
          // Stale session - clear and create fresh
          localStorage.removeItem("memorix:kioskSessionId");
          void createFreshSession();
        });
      return;
    }
    void createFreshSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const priceSummary = useMemo(() => {
    if (!catalog) return null;
    const imageAmount = packCount * catalog.tenant.price_per_image_paise;
    const videoAmount =
      videoEnabled && catalog.tenant.price_per_video_paise
        ? catalog.tenant.price_per_video_paise
        : 0;
    return {
      imageAmount,
      videoAmount,
      amountPaise: imageAmount + videoAmount,
      currency: catalog.tenant.currency,
    };
  }, [catalog, packCount, videoEnabled]);

  // Fetch session status immediately when entering slots step
  useEffect(() => {
    if (step !== "slots" || !sessionId) return;
    // Immediate fetch on step entry
    apiFetch<SessionStatusResponse>(`/api/kiosk/sessions/${sessionId}`)
      .then(setSessionStatus)
      .catch(() => {});
  }, [step, sessionId]);

  // Auto-configure ALL empty slots with defaults when entering slots step
  const allSlotsConfigured =
    sessionStatus?.session?.allSlotsConfigured ?? false;
  useEffect(() => {
    if (step !== "slots" || !catalog || !sessionId || !sessionStatus) return;
    if (catalog.scenePacks.length === 0) return;

    // Check if we need to auto-configure (not all slots configured yet)
    if (!allSlotsConfigured) {
      // Apply defaults to all empty slots
      const defaultConfig = {
        scenePackId: catalog.scenePacks[0].id,
        cameoIds: [] as string[],
        style: "photoreal",
        goofyPrompt: null,
      };
      void applyToAllEmpty(defaultConfig, true); // silent = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, catalog, sessionId, allSlotsConfigured]);

  // Poll while previewing or configuring slots (to get sync updates)
  useEffect(() => {
    if (!sessionId) return;
    if (step !== "preview" && step !== "qr" && step !== "slots") return;
    let stopped = false;
    const t = setInterval(async () => {
      if (stopped) return;
      try {
        const s = await apiFetch<SessionStatusResponse>(
          `/api/kiosk/sessions/${sessionId}`,
        );
        setSessionStatus(s);

        // Sync local slot configs from server if needed (optional, but good for resuming)
        if (s.slots) {
          setSlotConfigs((prev) => {
            const next = { ...prev };
            s.slots.forEach((slot) => {
              if (slot.scenePackId && !next[slot.slot_index]) {
                // Only backfill if we don't have it locally
              }
            });
            return next;
          });
        }
      } catch {
        // ignore
      }
    }, 2500);
    return () => {
      stopped = true;
      clearInterval(t);
    };
  }, [sessionId, step]);

  async function patchSession(patch: any) {
    if (!sessionId) return;
    try {
      await apiFetch(`/api/kiosk/sessions/${sessionId}`, {
        method: "PATCH",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(patch),
      });
    } catch (err: any) {
      // Session doesn't exist (stale localStorage) - create fresh
      if (err?.status === 404) {
        localStorage.removeItem("memorix:kioskSessionId");
        toast.error("Session expired, creating new one...");
        await createFreshSession();
        return;
      }
      throw err;
    }
  }

  async function configureSlot(slotIndex: number, config: any) {
    if (!sessionId) return;
    // Optimistic update
    setSlotConfigs((prev) => ({ ...prev, [slotIndex]: config }));

    // Backend uses 1-indexed slots
    await apiFetch(`/api/kiosk/sessions/${sessionId}/slots/${slotIndex + 1}`, {
      method: "PATCH",
      headers: { "content-type": "application/json" },
      body: JSON.stringify(config),
    });
    // Trigger a status refresh
    const s = await apiFetch<SessionStatusResponse>(
      `/api/kiosk/sessions/${sessionId}`,
    );
    setSessionStatus(s);
  }

  async function applyToAllEmpty(config: any, silent = false) {
    if (!sessionId) return;
    setBusy(true);
    try {
      await apiFetch(`/api/kiosk/sessions/${sessionId}/slots/apply-all`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify(config),
      });
      const s = await apiFetch<SessionStatusResponse>(
        `/api/kiosk/sessions/${sessionId}`,
      );
      setSessionStatus(s);
      // Update local slotConfigs from server response
      const newConfigs: typeof slotConfigs = {};
      s.slots.forEach((slot) => {
        if (slot.scenePackId) {
          // Use 0-indexed keys (slot_index from backend is 1-indexed)
          newConfigs[slot.slot_index - 1] = {
            scenePackId: slot.scenePackId,
            cameoIds: slot.cameoIds || [],
            style: slot.style,
            goofyPrompt: slot.goofyPrompt,
          };
        }
      });
      setSlotConfigs(newConfigs);
      if (!silent) toast.success("Applied to all empty slots");
    } catch (err: any) {
      toast.error("Failed to apply", { description: err?.message });
    } finally {
      setBusy(false);
    }
  }

  async function uploadSelfie(blob: Blob) {
    if (!sessionId) return;
    const form = new FormData();
    form.append(
      "file",
      new File([blob], "selfie.jpg", { type: blob.type || "image/jpeg" }),
    );
    await apiFetch(`/api/kiosk/sessions/${sessionId}/selfie`, {
      method: "POST",
      body: form,
    });
  }

  async function loadRazorpayScript() {
    if ((window as any).Razorpay) return;
    await new Promise<void>((resolve, reject) => {
      const script = document.createElement("script");
      script.src = "https://checkout.razorpay.com/v1/checkout.js";
      script.onload = () => resolve();
      script.onerror = () => reject(new Error("Failed to load Razorpay"));
      document.body.appendChild(script);
    });
  }

  async function startPayment() {
    if (!sessionId || !priceSummary) return;
    setBusy(true);
    try {
      // Create order
      const order = await apiFetch<{
        keyId: string;
        orderId: string;
        amountPaise: number;
        currency: string;
      }>(`/api/kiosk/sessions/${sessionId}/orders`, {
        method: "POST",
        headers: { "content-type": "application/json" },
        body: JSON.stringify({}),
        idempotencyKey: crypto.randomUUID(),
      });

      if (!order.keyId) {
        toast.error(
          "Payments not configured (missing Razorpay keyId on server)",
        );
        return;
      }

      await loadRazorpayScript();

      const rzp = new (window as any).Razorpay({
        key: order.keyId,
        amount: order.amountPaise,
        currency: order.currency,
        name: "Memorix",
        description: `${packCount} image pack`,
        order_id: order.orderId,
        handler: async (resp: any) => {
          try {
            await apiFetch(`/api/kiosk/sessions/${sessionId}/orders/verify`, {
              method: "POST",
              headers: { "content-type": "application/json" },
              body: JSON.stringify({
                razorpay_order_id: resp.razorpay_order_id,
                razorpay_payment_id: resp.razorpay_payment_id,
                razorpay_signature: resp.razorpay_signature,
              }),
              idempotencyKey: crypto.randomUUID(),
            });
            toast.success("Payment verified");
            setStep("preview");
          } catch (err: any) {
            toast.error("Payment verification failed", {
              description: err?.body?.error || err?.message,
            });
          }
        },
        modal: {
          ondismiss: () => toast.message("Payment cancelled"),
        },
        prefill: {},
        theme: { color: "#111827" },
      });
      rzp.open();
    } catch (err: any) {
      toast.error("Payment start failed", {
        description: err?.body?.error || err?.message,
      });
    } finally {
      setBusy(false);
    }
  }

  async function createDownloadLink() {
    if (!sessionId) return;
    setBusy(true);
    try {
      const res = await apiFetch<{ url: string }>(
        `/api/kiosk/sessions/${sessionId}/download-link`,
        {
          method: "POST",
        },
      );
      setDownloadUrl(res.url);
      toast.success("QR link ready");
    } catch (err: any) {
      toast.error("Failed to create download link", {
        description: err?.body?.error || err?.message,
      });
    } finally {
      setBusy(false);
    }
  }

  async function regenSlot(slotIndex: number) {
    // slotIndex here is 1-indexed (from backend slot.slot_index)
    if (!sessionId || !catalog) return;

    // Get config from local state (0-indexed) or from session status
    let config = slotConfigs[slotIndex - 1];
    if (!config?.scenePackId) {
      // Fallback to session status slot data
      const slot = sessionStatus?.slots.find((s) => s.slot_index === slotIndex);
      if (!slot?.scenePackId || !slot?.style) {
        toast.error("Cannot regenerate: missing config");
        return;
      }
      config = {
        scenePackId: slot.scenePackId,
        cameoIds: slot.cameoIds || [],
        style: slot.style,
        goofyPrompt: slot.goofyPrompt,
      };
    }

    setBusy(true);
    try {
      await apiFetch(
        `/api/kiosk/sessions/${sessionId}/slots/${slotIndex}/regenerate`,
        {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify(config),
          idempotencyKey: crypto.randomUUID(),
        },
      );
      toast.success("Regenerating…");
    } catch (err: any) {
      toast.error("Regen failed", {
        description: err?.body?.error || err?.message,
      });
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-screen bg-black text-white">
      <div className="mx-auto max-w-6xl px-6 py-8">
        <div className="flex items-center justify-between gap-4">
          <div>
            <div className="text-sm text-white/60">
              {catalog?.tenant?.name ?? "Venue"}
            </div>
            <h1 className="text-3xl font-semibold tracking-tight">
              Memorix Kiosk
            </h1>
          </div>
          <Button
            variant="outline"
            className="h-12 border-white/15 bg-white/5 text-white hover:bg-white/10"
            onClick={createFreshSession}
            disabled={busy}
          >
            <RefreshCcw className="mr-2 h-4 w-4" /> New guest
          </Button>
        </div>

        <div className="mt-6 flex flex-wrap gap-2 text-sm">
          {(["pack", "selfie", "slots", "pay", "preview", "qr"] as Step[]).map(
            (s) => (
              <div
                key={s}
                className={`rounded-full px-3 py-1 ${step === s ? "bg-white text-black" : "bg-white/10 text-white/70"}`}
              >
                {s}
              </div>
            ),
          )}
        </div>

        <div className="mt-8">
          {!catalog ? (
            <div className="flex items-center gap-3 text-white/70">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading catalog…
            </div>
          ) : null}

          {step === "pack" ? (
            <Card className="border-white/10 bg-white/5 text-white">
              <CardHeader>
                <CardTitle className="text-2xl">Choose your pack</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Image pack tiles - toggleable (click to select, click again to deselect) */}
                <div className="grid gap-4 md:grid-cols-4">
                  {[5, 10, 15, 20].map((n) => (
                    <button
                      key={n}
                      onClick={() => {
                        // Toggle: if already selected, deselect (set to 0); otherwise select
                        if (packCount === n) {
                          setPackCount(0);
                        } else {
                          setPackCount(n);
                        }
                      }}
                      className={`rounded-2xl border p-6 text-left transition ${
                        packCount === n
                          ? "border-white bg-white text-black"
                          : "border-white/10 bg-white/5 hover:bg-white/10"
                      }`}
                    >
                      <div className="text-2xl font-semibold">{n}</div>
                      <div className="mt-1 text-sm opacity-70">images</div>
                    </button>
                  ))}
                </div>

                {/* Video toggle card - only show if tenant has video pricing */}
                {catalog?.tenant.price_per_video_paise ? (
                  <button
                    onClick={() => setVideoEnabled(!videoEnabled)}
                    className={`w-full rounded-2xl border p-5 text-left transition ${
                      videoEnabled
                        ? "border-white bg-white text-black"
                        : "border-white/10 bg-white/5 hover:bg-white/10"
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <div className="text-lg font-semibold">AI Video</div>
                        <div className="mt-1 text-sm opacity-70">
                          8-second AI-generated video
                        </div>
                      </div>
                      <div className="text-xl font-semibold">
                        {videoEnabled
                          ? `₹${catalog.tenant.price_per_video_paise / 100}`
                          : "Off"}
                      </div>
                    </div>
                  </button>
                ) : null}

                <div className="flex items-center justify-between gap-3">
                  <div className="text-white/70">
                    <div>
                      {imagesEnabled && videoEnabled ? (
                        <>
                          Images: ₹{(priceSummary?.imageAmount ?? 0) / 100} +
                          Video: ₹{(priceSummary?.videoAmount ?? 0) / 100}
                        </>
                      ) : imagesEnabled ? (
                        <>Images: ₹{(priceSummary?.imageAmount ?? 0) / 100}</>
                      ) : videoEnabled ? (
                        <>Video: ₹{(priceSummary?.videoAmount ?? 0) / 100}</>
                      ) : (
                        <span className="text-red-400">
                          Select images or video
                        </span>
                      )}
                    </div>
                    <div className="text-lg font-semibold text-white">
                      Total: ₹{(priceSummary?.amountPaise ?? 0) / 100}
                    </div>
                  </div>
                  <Button
                    className="h-14 px-8 text-lg"
                    disabled={
                      busy || !sessionId || (!imagesEnabled && !videoEnabled)
                    }
                    onClick={async () => {
                      // Validate at least one is selected
                      if (!imagesEnabled && !videoEnabled) {
                        toast.error("Please select images or video");
                        return;
                      }
                      setBusy(true);
                      try {
                        await patchSession({
                          desiredPackCount: packCount,
                          videoEnabled,
                        });
                        setStep(nextStep(step));
                      } catch (err: any) {
                        toast.error("Failed to continue", {
                          description: err?.message,
                        });
                      } finally {
                        setBusy(false);
                      }
                    }}
                  >
                    {busy ? "Working…" : "Continue"}
                  </Button>
                </div>
              </CardContent>
            </Card>
          ) : null}

          {step === "selfie" ? (
            <Card className="border-white/10 bg-white/5 text-white">
              <CardHeader>
                <CardTitle className="text-2xl">Take a selfie</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <CameraCapture
                  onCapture={uploadSelfie}
                  onConfirm={() => setStep(nextStep(step))}
                />
              </CardContent>
            </Card>
          ) : null}

          {step === "slots" && catalog ? (
            <div className="space-y-6">
              {(() => {
                const totalSlots =
                  sessionStatus?.session?.totalSlots ??
                  (videoEnabled ? packCount + 1 : packCount);
                const currentSlot = sessionStatus?.slots?.[currentSlotIndex];
                const isVideoSlot = currentSlot?.isVideo ?? false;

                return (
                  <>
                    <SlotConfigurator
                      slotIndex={currentSlotIndex}
                      totalSlots={totalSlots}
                      scenePacks={catalog.scenePacks}
                      cameos={catalog.cameos}
                      isVideo={isVideoSlot}
                      currentConfig={
                        slotConfigs[currentSlotIndex] || {
                          scenePackId: null,
                          cameoIds: [],
                          style: null,
                          goofyPrompt: null,
                        }
                      }
                      onConfigChange={(cfg) =>
                        configureSlot(currentSlotIndex, cfg)
                      }
                      onApplyToAll={() =>
                        applyToAllEmpty(slotConfigs[currentSlotIndex])
                      }
                      onPrev={() =>
                        setCurrentSlotIndex((i) => Math.max(0, i - 1))
                      }
                      onNext={() =>
                        setCurrentSlotIndex((i) =>
                          Math.min(totalSlots - 1, i + 1),
                        )
                      }
                      canGoPrev={currentSlotIndex > 0}
                      canGoNext={currentSlotIndex < totalSlots - 1}
                    />

                    <div className="flex justify-end">
                      {sessionStatus?.session?.allSlotsConfigured ? (
                        <Button
                          className="h-14 px-10 text-lg"
                          onClick={() => setStep("pay")}
                        >
                          Continue to Payment
                        </Button>
                      ) : (
                        <Button
                          className="h-14 px-10 text-lg"
                          onClick={() => {
                            const slots = sessionStatus?.slots;
                            const totalSlots = slots?.length ?? packCount;
                            if (!slots || totalSlots <= 0) {
                              setCurrentSlotIndex((i) =>
                                Math.min(totalSlots - 1, i + 1),
                              );
                              return;
                            }

                            for (
                              let offset = 1;
                              offset <= totalSlots;
                              offset++
                            ) {
                              const idx =
                                (currentSlotIndex + offset) % totalSlots;
                              if (!slots[idx]?.scenePackId) {
                                setCurrentSlotIndex(idx);
                                return;
                              }
                            }

                            setCurrentSlotIndex((i) =>
                              Math.min(totalSlots - 1, i + 1),
                            );
                          }}
                        >
                          Next
                        </Button>
                      )}
                    </div>
                  </>
                );
              })()}
            </div>
          ) : null}

          {step === "pay" ? (
            <Card className="border-white/10 bg-white/5 text-white">
              <CardHeader>
                <CardTitle className="text-2xl">Pay & Generate</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-4 md:grid-cols-2">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                    <div className="text-sm text-white/70">Pack</div>
                    <div className="mt-2 text-2xl font-semibold">
                      {packCount === 0
                        ? "Video only"
                        : `${packCount} images${videoEnabled ? " + video" : ""}`}
                    </div>
                  </div>
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-5">
                    <div className="text-sm text-white/70">
                      Slots Configured
                    </div>
                    <div className="mt-2 text-2xl font-semibold">
                      {(() => {
                        const configuredSlots =
                          sessionStatus?.session?.configuredSlots ?? 0;

                        if (packCount === 0) {
                          return configuredSlots >= 1
                            ? "1/1 video"
                            : "0/1 video";
                        }

                        const configuredImages = Math.min(
                          configuredSlots,
                          packCount,
                        );

                        return (
                          <>
                            {configuredImages}/{packCount} images
                            {videoEnabled ? " + 1 video" : null}
                          </>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                <div className="flex items-center justify-between gap-3">
                  <div className="text-white/70">
                    Total:{" "}
                    <span className="font-semibold text-white">
                      ₹{(priceSummary?.amountPaise ?? 0) / 100}
                    </span>
                  </div>
                  <Button
                    className="h-14 px-10 text-lg"
                    onClick={startPayment}
                    disabled={busy}
                  >
                    {busy ? "Working…" : "Pay with UPI"}
                  </Button>
                </div>
                <div className="text-sm text-white/60">
                  {packCount === 0
                    ? "After payment, your video will be generated. This may take a few minutes."
                    : "After payment, generation happens asynchronously. You can preview results and use regen tokens."}
                </div>
              </CardContent>
            </Card>
          ) : null}

          {step === "preview" ? (
            <Card className="border-white/10 bg-white/5 text-white">
              <CardHeader>
                <CardTitle className="text-2xl">Preview</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="flex flex-wrap items-center justify-between gap-3">
                  {/* Only show regen tokens if there are images (not video-only) */}
                  {packCount > 0 ? (
                    <div className="text-white/70">
                      Regen tokens:{" "}
                      <span className="font-semibold text-white">
                        {sessionStatus?.session.regen_tokens_remaining ?? "…"}
                      </span>
                    </div>
                  ) : (
                    <div />
                  )}
                  <Button
                    className="h-12 px-6"
                    onClick={() => setStep("qr")}
                    disabled={!sessionStatus}
                  >
                    Continue to QR
                  </Button>
                </div>

                <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
                  {(sessionStatus?.slots ?? []).map((slot) => (
                    <div
                      key={slot.id}
                      className="overflow-hidden rounded-2xl border border-white/10 bg-white/5"
                    >
                      <div
                        className={`${slot.isVideo ? "aspect-video" : "aspect-square"} bg-black/30`}
                      >
                        {slot.imageUrl ? (
                          slot.isVideo ? (
                            <video
                              src={slot.imageUrl}
                              className="h-full w-full object-cover"
                              controls
                              playsInline
                            />
                          ) : (
                            <img
                              src={slot.imageUrl}
                              className="h-full w-full object-cover"
                              alt={`Slot ${slot.slot_index}`}
                            />
                          )
                        ) : (
                          <div className="flex h-full items-center justify-center text-white/60">
                            {slot.status === "failed"
                              ? "Failed"
                              : "Generating…"}
                          </div>
                        )}
                      </div>
                      <div className="p-4">
                        <div className="flex items-center justify-between">
                          <div className="text-lg font-semibold">
                            {slot.isVideo ? "Video" : `#${slot.slot_index}`}
                          </div>
                          <div className="text-xs text-white/60">
                            {slot.status}
                          </div>
                        </div>
                        {/* Only show regenerate button for image slots */}
                        {!slot.isVideo && (
                          <div className="mt-3 flex gap-2">
                            <Button
                              className="h-11 flex-1"
                              variant="outline"
                              disabled={
                                !slot.imageUrl ||
                                (sessionStatus?.session
                                  .regen_tokens_remaining ?? 0) <= 0 ||
                                busy
                              }
                              onClick={() => regenSlot(slot.slot_index)}
                            >
                              Regenerate
                            </Button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))}
                </div>

                {!sessionStatus ? (
                  <div className="text-white/60">Loading session…</div>
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          {step === "qr" ? (
            <Card className="border-white/10 bg-white/5 text-white">
              <CardHeader>
                <CardTitle className="text-2xl">Scan to download</CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                <div className="grid gap-8 md:grid-cols-2">
                  <div className="space-y-4">
                    <div className="text-white/70">
                      Generate a secure download link, then scan the QR on your
                      phone.
                    </div>
                    <Button
                      className="h-14 text-lg"
                      onClick={createDownloadLink}
                      disabled={busy}
                    >
                      {busy ? "Working…" : "Generate QR"}
                    </Button>
                    {downloadUrl ? (
                      <div className="break-all rounded-xl border border-white/10 bg-white/5 p-4 text-sm text-white/70">
                        {downloadUrl}
                      </div>
                    ) : null}
                  </div>

                  <div className="flex items-center justify-center">
                    {downloadUrl ? (
                      <QrCode value={downloadUrl} />
                    ) : (
                      <div className="h-[320px] w-[320px] rounded-xl bg-white/5" />
                    )}
                  </div>
                </div>

                <div className="rounded-2xl border border-white/10 bg-white/5 p-6 opacity-60">
                  <div className="flex items-center gap-2">
                    <div className="text-lg font-semibold">
                      Email Notifications
                    </div>
                    <span className="rounded-full bg-amber-500/20 px-2 py-0.5 text-xs font-medium text-amber-400">
                      Coming Soon
                    </span>
                  </div>
                  <div className="mt-2 text-white/70">
                    Email delivery is coming soon. For now, use the QR code to
                    access your photos and videos.
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : null}
        </div>
      </div>
    </div>
  );
}
